import { Service, inject } from '@angular/core';
import { HttpBasicRequest } from 'app/shared/models/http-basic-request.model';
import { PagedResponse } from 'app/shared/models/paged-response.model';
import { Shop } from 'app/shared/models/shop.model';
import { BasicHttpClientService } from 'app/shared/services/basic-http-client.service';
import { UtilityFunctions } from 'app/shared/utilities/functions.models';
import { environment } from 'environments/environment';
import { Observable, of, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { MediaDTO } from '../../../../shared/models/media.models';
import { TierAvailabilityStatus, TierDTO, TierVM } from '../../models/settings/tier.model';
import { SettingMapper } from '../../utilities/setting-mapper';
import { ShopContextStore } from 'app/shared/state/shop-context.store';
import { StorePointOfSaleInputModel, StorePointOfSaleVM } from '../../models/point-of-sale/storePointOfSale.model';
import { AssortmentDTO } from '../../models/products/assortment.model';
import { VisibilityMediaType } from '../../enum/visibility-media-type.enum';
import { AssortmentMultiSelectVM } from '../../models/settings/assortment.model';

@Service()
export class SettingService {
  private _httpClient = inject(BasicHttpClientService);

  private _baseUrl = environment.urls.apiUrl.url;

  private _shopContext = inject(ShopContextStore);

  getTiers(loaderId?: string, skipCount?: number, maxResultCount?: number, search: string = '', sorting?: string, availabilityStatus?: TierAvailabilityStatus, channelId?: number) {
    const storeId = this._shopContext.currentShop().id;
    return this.getTiersByStoreId(storeId, loaderId, skipCount, maxResultCount, search, sorting, availabilityStatus, channelId);
  }

  getTiersByStoreId(storeId: number, loaderId?: string, skipCount?: number, maxResultCount?: number, search: string = '', sorting?: string, availabilityStatus?: TierAvailabilityStatus, channelId?: number,) {
    const params = {
      skipCount: skipCount,
      maxResultCount: maxResultCount,
      searchString: search,
      sorting: sorting || 'name',
      storeId: storeId,
    };
    if (channelId) params['channelId'] = channelId;
    if (availabilityStatus) params['availabilityStatus'] = availabilityStatus;

    const subject = new Subject<PagedResponse<TierVM>>();
    this._httpClient.get<PagedResponse<TierDTO>>(
      this._baseUrl + '/tiers',
      { params },
      loaderId
    ).subscribe(data => {
      const res = new PagedResponse<TierVM>();
      res.items = data.items.map(x => SettingMapper.tierVM(x));
      res.totalCount = data.totalCount;
      subject.next(res);
    });
    return subject.asObservable();
  }

  getMultipleTiersById(tiersId: number[], loaderId: string) {
    const tiersRequest: HttpBasicRequest[] = tiersId.filter(x => !!x).map(id => { return { url: this._baseUrl + `/tiers/${id}`, method: 'get' }; });
    return this._httpClient.forkJoin<TierDTO>(tiersRequest, loaderId);
  }

  getTierById(tierId: number, loaderId: string): Observable<TierDTO> {
    return this._httpClient.get(`${this._baseUrl}/tiers/${tierId}`, {}, loaderId);
  }

  createOrEditTier(tier: TierDTO, loaderId: string, variantToAdd: Number[], variantToDelete: Number[]) {
    return tier.id ? this.editTier(tier.id, tier, loaderId, variantToAdd, variantToDelete) : this.createTier(tier, loaderId, variantToAdd);
  }

  deleteTier(tierId: number, loaderId: string): Observable<any> {
    return this._httpClient.delete(this._baseUrl + `/tiers/${tierId}`, {}, loaderId);
  }

  saveStoreSettings(currentShop: Shop, cacheShop: Shop, loaderId) {
    let requests: HttpBasicRequest[] = [];
    currentShop.activatedStoreChannels.filter(channel =>
      !cacheShop.activatedStoreChannels.some(channel2 =>
        UtilityFunctions.isEquals(channel, channel2))).forEach(activatedChannel => {
          requests.push({
            url: `${this._baseUrl}/stores/${currentShop.id}/store-channel/${activatedChannel.channel.id}`, method: 'put', body: {
              senderEmailAddress: activatedChannel.senderEmailAddress,
              notificationEmailAddress: activatedChannel.notificationEmailAddress,
              shippingPrice: activatedChannel.shippingPrice ?? 0,
              freeShippingOrderTotalThreshold: activatedChannel.freeShippingOrderTotalThreshold,
            }
          });
        });

    const defaultDesignerIsChange = !UtilityFunctions.isEquals(currentShop.defaultCustomizationRequestsDesignerUser, cacheShop.defaultCustomizationRequestsDesignerUser);
    if (defaultDesignerIsChange && cacheShop.defaultCustomizationRequestsDesignerUser?.id) {
      requests.push({
        url: `${this._baseUrl}/stores/${currentShop.id}/default-designer-user`,
        method: 'delete',
      });
    }

    return new Observable((observer) => {
      observer.next(null);
      observer.complete();
    }).pipe(
      switchMap(() => requests.length > 0 ? this._httpClient.forkJoin(requests, loaderId) : of(null)),
      switchMap(() => {
        const newRequests: HttpBasicRequest[] = [
          { method: 'put', url: `${this._baseUrl}/stores/${currentShop.id}`, body: currentShop }
        ]
        if (defaultDesignerIsChange && currentShop.defaultCustomizationRequestsDesignerUser?.id) {
          newRequests.push({
            url: `${this._baseUrl}/stores/${currentShop.id}/default-designer-user/${currentShop.defaultCustomizationRequestsDesignerUser.id}`,
            method: 'post',
          });
        }
        return this._httpClient.forkJoin(newRequests, loaderId);
      }),
      switchMap(() => this._httpClient.get<Shop>(this._baseUrl + '/stores/' + currentShop.id, null, loaderId)),
    );
  }

  uploadShopImage(file: File, mediaParam, loaderId): Observable<MediaDTO> {
    var result = new Subject<MediaDTO>();
    const formData = new FormData();
    formData.append('file', file);

    this._httpClient.post(this._baseUrl + '/media/upload-media', formData, {
      params: mediaParam
    }, loaderId).subscribe((res: MediaDTO) => {
      result.next(res);
    });
    return result.asObservable();
  }

  private createTier(tier: TierDTO, loaderId: string, variantToAdd: Number[]) {
    const subject = new Subject<void>();
    this._httpClient.post(this._baseUrl + '/tiers', tier, {}, loaderId).subscribe(res => {
      this.associateVariantsToTier(loaderId, res.id, variantToAdd).subscribe(() => {
        subject.next(res);
      });
    });
    return subject.asObservable();
  }

  editTier(tierId: number, tier: TierDTO, loaderId: string, variantToAdd: Number[] = [], variantToDelete: Number[] = []) {
    const subject = new Subject<void>();
    this._httpClient.put(this._baseUrl + `/tiers/${tierId}`, tier, {}, loaderId).subscribe(() => {
      const requests: HttpBasicRequest[] = [];
      if (variantToDelete?.length)
        requests.push({ url: `${this._baseUrl}/tiers/${tierId}/disassociate-variants`, method: 'post', body: variantToDelete });
      if (variantToAdd?.length)
        requests.push({ url: `${this._baseUrl}/tiers/${tierId}/associate-variants`, method: 'post', body: variantToAdd });
      requests.length > 0 ?
        this._httpClient.forkJoin(requests, loaderId)
          .subscribe(() => subject.next(undefined)) :
        subject.next(undefined);
    });

    return subject.asObservable();
  }

  archiveTier(tierId: number, loaderId: string) {
    return this._httpClient.post(`${this._baseUrl}/tiers/${tierId}/archive`, null, {}, loaderId);
  }

  associateVariantsToTier(loaderId: string, tierId: Number, variantToAdd: Number[]) {
    return this._httpClient.post(`${this._baseUrl}/tiers/${tierId}/associate-variants`, variantToAdd, {}, loaderId);
  }

  disassociateVariantsFromTier(loaderId: string, tierId: Number, variantToDelete: Number[]) {
    return this._httpClient.post(`${this._baseUrl}/tiers/${tierId}/disassociate-variants`, variantToDelete, {}, loaderId);
  }

  getStorePointOfSales(loaderId: string, storeId: number, params: StorePointOfSaleInputModel) {
    return this._httpClient.get<PagedResponse<StorePointOfSaleVM>>(`${this._baseUrl}/stores/${storeId}/point-of-sales`, { params }, loaderId);
  }

  updateStorePointsOfSale(loaderId: string, storeId: number, params: any) {
    return this._httpClient.put(`${this._baseUrl}/stores/${storeId}/bulk-update-tier-point-of-sale`, params, {}, loaderId);
  }

  getStoreAssortments(loaderId: string, skipCount: number, maxResultCount: number, search: string = '', sorting?: string) {
    const params = {
      storeId: this._shopContext.currentShop()?.id,
      skipCount: skipCount.toString(),
      maxResultCount: maxResultCount.toString(),
      searchString: search || '',
      visibilityMedia: VisibilityMediaType.ShowAllMedia,
      sorting: sorting || 'title'
    };

    const url = this._baseUrl + '/assortments';
    return this._httpClient.get<PagedResponse<AssortmentDTO>>(url, {
      params
    }, loaderId).pipe(map(data => new PagedResponse<AssortmentMultiSelectVM>({
      items: data.items.map(x => new AssortmentMultiSelectVM(x)),
      totalCount: data.totalCount
    })));
  }
  getAssortment(assortmentId: number, loaderId?: string): Observable<AssortmentDTO> {
    const url = this._baseUrl + '/assortments/' + assortmentId;
    return this._httpClient.get<AssortmentDTO>(url, {}, loaderId);
  }
}





